import { CalendarDays } from 'lucide-react'

import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { formatShortDate } from '@/lib/datetime'

import { BACKDATING_WINDOW_DAYS, earliestValueDate, isValueDateAllowed } from './constants'

/**
 * The date the sheet's money moved.
 *
 * It is not the same fact as the branch's business day, and conflating the two is how a week of
 * paper sheets ends up dated the Friday somebody sat down to key them. The business day is the
 * ledger day a posting *lands* on and comes from the server; the value date is the day the money
 * *moved* and comes off the sheet.
 *
 * The window is three days, from `SavingsPostingService.BackdatingWindowDays`, and the field
 * enforces it here so the operator finds out before keying forty lines rather than on the ninth
 * refusal. The loans contract is looser — it only refuses a date before disbursement — but a sheet
 * carries one value date across all three columns, so the tighter rule governs the whole sheet.
 */
export function ValueDateField({
  value,
  onChange,
  businessDate,
  meetingDate,
  disabled,
}: {
  value: string
  onChange: (value: string) => void
  businessDate: string
  /** The day the meeting was held, which is the honest value date when it is still in range. */
  meetingDate: string
  disabled: boolean
}) {
  const earliest = earliestValueDate(businessDate)
  const allowed = isValueDateAllowed(value, businessDate)
  const meetingInRange = isValueDateAllowed(meetingDate, businessDate)

  return (
    <div className="space-y-1.5">
      <Label htmlFor="collection-value-date">Value date</Label>

      <div className="flex items-center gap-2">
        <CalendarDays className="size-4 shrink-0 text-muted" aria-hidden />
        <Input
          id="collection-value-date"
          type="date"
          value={value}
          min={earliest}
          max={businessDate}
          disabled={disabled}
          aria-invalid={!allowed}
          aria-describedby="collection-value-date-help"
          onChange={(event) => onChange(event.target.value)}
          className="w-48"
        />
      </div>

      <p id="collection-value-date-help" className="text-xs text-muted">
        {allowed ? (
          <>
            Between {formatShortDate(earliest)} and {formatShortDate(businessDate)} — today or up to{' '}
            {BACKDATING_WINDOW_DAYS} days back, never forward.
          </>
        ) : (
          <span className="text-danger-text">
            Outside the permitted window. Every line would be refused as{' '}
            <span className="font-mono">savings.value_date_out_of_window</span>.
          </span>
        )}
      </p>

      {!meetingInRange ? (
        <p className="text-xs text-warning-text">
          This meeting was held on {formatShortDate(meetingDate)}, which is further back than the
          window allows. The sheet can only be dated {formatShortDate(earliest)} at the earliest, so
          the ledger will carry a later date than the paper. Say so on the sheet.
        </p>
      ) : null}
    </div>
  )
}
